import { CHUNK_RELOAD_FLAG, isStaleChunkError } from "@/lib/chunk-errors";
import { Sentry } from "@/lib/sentry";

import { GxButton } from "../../../../../.packages/galaxia-pro";

/**
 * `ErrorView` — fallback para `Sentry.ErrorBoundary` y para flujos de
 * data fetch que produzcan `isError`. NO loguea (Principio VIII);
 * el reporte a Sentry lo hace el ErrorBoundary o `useQuery` cuando
 * el caller invoque `Sentry.captureException`.
 *
 * Detección de stale-chunk vive en `lib/chunk-errors.ts` (única
 * fuente de verdad compartida con `main.tsx` y `sentry.ts`).
 */
export interface ErrorViewProps {
  error?: Error | unknown;
  resetError?: () => void;
}

export function ErrorView({ error, resetError }: ErrorViewProps) {
  const chunkError = isStaleChunkError(error);

  // Reporte explícito al hub solo para errores REALES (no stale-chunk
  // — el filtro BeforeSend ya los descarta, pero capturar acá igual
  // sumaría ruido al inbox). El ErrorBoundary hace su propia captura
  // para los demás casos.
  if (error instanceof Error && !chunkError) {
    Sentry.captureException(error);
  }

  // Stale-chunk: hard reload automático sin intervención del usuario.
  // El user reportó "está feo eso" — no debería ver siquiera el
  // ErrorView si el problema es solo "deploy nuevo, cache vieja".
  // Solo nos limitamos a UNA recarga por sesión (flag) para no caer
  // en loop si el bundle nuevo también rompe.
  if (
    chunkError &&
    globalThis.sessionStorage !== undefined &&
    !globalThis.sessionStorage.getItem(CHUNK_RELOAD_FLAG)
  ) {
    globalThis.sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
    globalThis.location.reload();
    // Mientras el browser procesa el reload, mostramos un mensaje
    // breve en lugar del CTA "Reintentar" (evita que el user toque
    // antes de que reload despache).
    return (
      <div role="status" aria-live="polite" className="op-error-view">
        <h2>Cargando versión nueva…</h2>
        <p>Refrescando automáticamente.</p>
      </div>
    );
  }

  // Para errores no-chunk: intentar resetError primero (puede
  // recuperar sin perder estado del SPA); si no hay reset, reload
  // como último recurso. Nunca queda sin acción posible.
  function handleRetry(): void {
    if (chunkError) {
      globalThis.location.reload();
      return;
    }
    if (resetError) {
      resetError();
      return;
    }
    globalThis.location.reload();
  }

  return (
    <div role="alert" aria-live="assertive" className="op-error-view">
      <h2>Algo no funcionó como debía</h2>
      <p>
        {chunkError
          ? "Hay una versión nueva de la app. Recarga para tomarla."
          : "Se notificó al equipo. Podés intentar nuevamente; si persiste, contacta a soporte."}
      </p>
      <GxButton
        type="button"
        variant="accent"
        onClick={handleRetry}
        className="op-error-view__retry"
      >
        {chunkError ? "Recargar" : "Reintentar"}
      </GxButton>
    </div>
  );
}
